import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, ListItemButton } from '@mui/material';
import styles from './SidebarButton.module.scss';
import cx from 'classnames';
import { useRecoilValue } from 'recoil';
import { sidebarExtendedState } from '../../../../../atoms/layout/layout.atom';
import { useWindowWidth } from '@react-hook/window-size';
import { colors } from '../../../../../styles/colors';
import { routes } from '../../../../../../config/routes/routes';
import { translationsState } from '../../../../../atoms/translation/translation.atom';
import { translate } from '../../../../../utils/translation/translation';
import { useMutation } from '@tanstack/react-query';
import type { DefaultError } from '@tanstack/query-core';
import axios from '../../../../../libraries/axios/axios';
import Label, { LabelVariant } from '../../../../../common/Label/Label';

export type SidebarItem = {
  title: string;
  icon: ReactNode;
  url: string;
  apiUrl?: string;
  countApiUrl?: string;
  enabled?: boolean;
};

type SidebarButtonProps = {
  item: SidebarItem;
};

export const SidebarButton = ({ item }: SidebarButtonProps) => {
  const translations = useRecoilValue(translationsState);
  const sidebarExtendedValue = useRecoilValue(sidebarExtendedState);

  const [count, setCount] = useState<number>(0);

  const location = useLocation();
  const screenWidth = useWindowWidth();

  const { mutate: getCount } = useMutation<{ data: number }, DefaultError>({
    mutationFn: () => {
      return axios.get(item.countApiUrl ?? '');
    },
    onSuccess: (response) => {
      setCount(response?.data ?? 0);
    },
  });

  useEffect(() => {
    if (!item.countApiUrl) {
      return;
    }

    getCount();
  }, [item]);

  const isCollapsed = useMemo(() => {
    if (screenWidth < 1200) {
      return false;
    }

    return !sidebarExtendedValue;
  }, [sidebarExtendedValue, screenWidth]);

  const isActive = useMemo(() => {
    if (item.url === routes.admin.dashboard) {
      return location.pathname === item.url;
    }

    return location.pathname.includes(item.url);
  }, [item, location.pathname]);

  return (
    <NavLink to={item.url} className={styles.sidebarButton}>
      <ListItemButton
        className={cx(styles.itemButton, {
          [styles.active]: isActive,
          [styles.collapsed]: isCollapsed,
        })}
        style={{
          color: isActive ? colors.sidebarColor : colors.textSubtitleColor,
          backgroundColor: isActive ? colors.sidebarBackgroundColor : undefined,
        }}
      >
        <Box component="span" className={styles.iconContainer}>
          {item.icon}
        </Box>
        <Box component="span" className={styles.labelContainer}>
          <Box component="span" className={styles.label}>
            {translate(translations, item.title)}
          </Box>
        </Box>
        {count > 0 && (
          <Label text={count.toString()} variant={LabelVariant.error} />
        )}
      </ListItemButton>
    </NavLink>
  );
};

export default SidebarButton;
